import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api, formatApiError, resetSessionExpiredFlag } from "@/lib/api";
import { toast } from "sonner";
import { X, Lock } from "lucide-react";

const AdminContext = createContext(null);

export function AdminProvider({ children }) {
  const [admin, setAdmin] = useState(null); // null = checking, false = logged out, object = logged in
  const [reauth, setReauth] = useState(false); // show non-destructive re-login modal
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/me").then((res) => setAdmin(res.data)).catch(() => setAdmin(false));
  }, []);

  // When any admin API call reports an expired session, prompt re-login IN PLACE
  // (no navigation) so the current form/import is preserved and can be retried.
  useEffect(() => {
    const onExpired = () => {
      setAdmin((cur) => (cur && cur !== false ? cur : cur)); // keep current admin object/UI mounted
      setReauth(true);
    };
    window.addEventListener("admin-session-expired", onExpired);
    return () => window.removeEventListener("admin-session-expired", onExpired);
  }, []);

  const login = useCallback((user) => {
    resetSessionExpiredFlag();
    setAdmin(user);
  }, []);

  const logout = useCallback(() => {
    const p = api.post("/auth/logout").catch(() => {});
    try { localStorage.removeItem("jm_admin_token"); } catch (e) { /* ignore */ }
    setAdmin(false);
    return p;
  }, []);

  const submitReauth = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email: admin?.email, password: pw });
      resetSessionExpiredFlag();
      setReauth(false);
      setPw("");
      if (data?.user) setAdmin(data.user);
      toast.success("Signed back in — please retry your last action.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  const value = useMemo(() => ({ admin, login, logout }), [admin, login, logout]);

  return (
    <AdminContext.Provider value={value}>
      {children}
      {reauth && admin && admin !== false && (
        <div className="fixed inset-0 z-[100] bg-black/60 flex items-center justify-center p-4" data-testid="reauth-modal">
          <form onSubmit={submitReauth} className="bg-white rounded-xl w-full max-w-sm p-6">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl text-navy flex items-center gap-2"><Lock className="w-5 h-5 text-brick" /> Session expired</h2>
              <button type="button" onClick={() => setReauth(false)} className="text-navy/50 hover:text-brick"><X className="w-5 h-5" /></button>
            </div>
            <p className="mt-2 text-sm text-ink/70">Your admin session timed out. Sign in again to continue — <b>your unsaved work has been kept</b> on the page behind this box.</p>
            <label className="block text-sm font-bold text-navy uppercase mt-4">Password for {admin?.email}</label>
            <input data-testid="reauth-password" type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
              className="mt-1 w-full rounded-md border border-navy/20 px-3 py-3 text-base outline-none focus:border-brick" />
            <button data-testid="reauth-submit" disabled={busy}
              className="mt-5 w-full bg-brick text-white font-bold uppercase rounded-md py-3.5 hover:bg-navy transition-colors disabled:opacity-60">
              {busy ? "Signing in…" : "Sign back in"}
            </button>
          </form>
        </div>
      )}
    </AdminContext.Provider>
  );
}

export const useAdmin = () => useContext(AdminContext);
